// Case conversion helpers for the axios interceptor.
//
// CLAUDE.md (rule #2) declares that the gateway speaks
// snake_case on the wire and the frontend speaks camelCase in
// TypeScript. This module is the single place that bridges the
// two: request bodies are walked camelCase → snake_case before
// send, response bodies are walked snake_case → camelCase after
// receive.
//
// The interceptor in `api.ts` skips conversion for spec-dictated
// routes — see `isSpecDictatedUrl` below for the list. FHIR
// (R4/R5), CDS Hooks, and the OAuth 2.0 token endpoint all
// have wire formats fixed by external specs and must NOT be
// touched.

/** `"camelCase"` → `"camel_case"`. Idempotent on snake_case input. */
export function camelToSnake(str: string): string {
  return str.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`)
}

/** `"snake_case"` → `"snakeCase"`. Idempotent on camelCase input. */
export function snakeToCamel(str: string): string {
  return str.replace(/_([a-z])/g, (_, letter: string) => letter.toUpperCase())
}

/**
 * Recursively walk an object/array, applying `transformer` to
 * every key. Non-object values pass through unchanged. Date
 * instances and Files are preserved by reference so a form
 * upload doesn't get its binary blob walked into a key/value
 * map.
 */
function transformKeys(value: unknown, transformer: (key: string) => string): unknown {
  if (value === null || value === undefined) {
    return value
  }
  // Preserve Date, File, Blob, FormData, ArrayBuffer — anything
  // that isn't a plain object or array passes through.
  if (
    typeof value !== 'object' ||
    value instanceof Date ||
    value instanceof Blob ||
    value instanceof FormData ||
    value instanceof ArrayBuffer
  ) {
    return value
  }
  if (Array.isArray(value)) {
    return value.map((item) => transformKeys(item, transformer))
  }
  const result: Record<string, unknown> = {}
  for (const [key, val] of Object.entries(value as Record<string, unknown>)) {
    result[transformer(key)] = transformKeys(val, transformer)
  }
  return result
}

/** Deep-walk: every key from camelCase to snake_case. */
export function keysToSnake(value: unknown): unknown {
  return transformKeys(value, camelToSnake)
}

/** Deep-walk: every key from snake_case to camelCase. */
export function keysToCamel(value: unknown): unknown {
  return transformKeys(value, snakeToCamel)
}

/**
 * URL prefixes / substrings whose payloads MUST NOT be walked.
 * These routes have wire shapes fixed by external specs:
 *
 *  - `/fhir/r4/`, `/fhir/r5/`  — FHIR uses camelCase per its
 *    spec (`birthDate`, `resourceType`); converting to
 *    snake_case would corrupt every resource.
 *  - `/cds-services`            — CDS Hooks spec is camelCase.
 *  - `/auth/token`              — OAuth 2.0 / RFC 6749 mandates
 *    snake_case (`access_token`, `expires_in`); converting
 *    response keys to camelCase would break SMART app clients.
 *  - `/.well-known/`            — JWKS, SMART configuration,
 *    OpenID Discovery; all mixed snake/camel per RFC.
 */
const SPEC_DICTATED_FRAGMENTS: readonly string[] = [
  '/fhir/r4/',
  '/fhir/r5/',
  // `'/fhir/$export'` used to sit here. No such endpoint has ever existed —
  // bulk-export kick-off is `GET /fhir/r4/$export`, matched by the entries
  // above. The fragment was a fossil of the same mistake as the api.ts caller
  // it protected (wrong path AND wrong verb), and an exemption for a URL that
  // does not exist is worse than useless: it makes the broken path look
  // deliberate to the next reader.
  '/fhir/r4',
  '/fhir/r5',
  '/cds-services',
  '/auth/token',
  '/.well-known/',
]

export function isSpecDictatedUrl(url: string | undefined | null): boolean {
  if (!url) return false
  // Match the ROUTE, not the query string: config.url includes `?...`, so a
  // VynaEMR-owned request whose params contain e.g. `/fhir/r4` (a free-text
  // search value) would otherwise skip case conversion on both legs.
  // No `?? url`: String.split always returns a non-empty array, so [0] is
  // always a string — a nullish fallback would be an uncoverable branch
  // under this file's 100% coverage floor.
  const path = url.split('?', 1)[0]
  return SPEC_DICTATED_FRAGMENTS.some((fragment) => path.includes(fragment))
}
